import { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';

import {
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Spinner,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  CheckOutline,
  CountryCode,
  DropdownInput,
  DropdownOption,
  EarningsAccumulationMode,
  useToast,
} from '@aslanpay/portal-common-lib';

import ProfileSection from 'app/components/UI/ProfileSection';

import { CompanyFeeGroupLabels } from 'app/constants/companies';
import { countries } from 'app/constants/countries';

import useFetchCompany from 'app/hooks/useFetchCompany';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useUpdateCompany from 'app/hooks/useUpdateCompany';
import useUpdateCompanyConfiguration from 'app/hooks/useUpdateCompanyConfiguration';

import { generalConfigurationFormSchema } from 'app/utils/schemas';

import { GeneralConfigurationFormFields, GeneralTabProps } from './GeneralTab.types';

const EARNINGS_ACCUMULATION_MODE_OPTIONS: DropdownOption[] = [
  { value: 'CUMULATIVE', text: 'Cumulative' },
  { value: 'OVERRIDE', text: 'Override' },
];

const EARNINGS_ACCUMULATION_MODE_DESCRIPTIONS: Record<EarningsAccumulationMode, string> = {
  CUMULATIVE:
    'Cumulative — each approved earnings timesheet upload adds to the existing earnings for the pay cycle. Previous earnings are kept.',
  OVERRIDE:
    'Override — each approved earnings timesheet upload replaces all previously imported earnings for the same pay cycle.',
};

const COUNTRY_OPTIONS: DropdownOption[] = countries
  .filter((country) => country.code === 'GB')
  .map((country) => ({ value: country.code, text: country.details.name }));

function GeneralTab({ companyId = '' }: GeneralTabProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: company, isInitialLoading: isLoadingCompany } = useFetchCompany(companyId);
  const { data: companyConfiguration, isInitialLoading: isLoadingConfiguration } =
    useFetchCompanyConfiguration(companyId);

  const { mutateAsync: updateCompanyAsync } = useUpdateCompany({
    onSuccess: () => {},
    onError: () => {},
  });

  const { mutateAsync: updateCompanyConfigurationAsync } = useUpdateCompanyConfiguration({
    onSuccess: () => {},
    onError: () => {},
  });

  const [showAnnualGrossSalary, setShowAnnualGrossSalary] = useState(false);
  const [selectedEarningsMode, setSelectedEarningsMode] = useState<
    EarningsAccumulationMode | undefined
  >(undefined);
  const [pointsIfVisited, setPointsIfVisited] = useState('');
  const [pointsIfNotVisited, setPointsIfNotVisited] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const initialShowAnnualGrossSalary =
    companyConfiguration?.generalCompanyConfiguration?.showEmployeeAnnualGrossSalary ?? false;
  const initialEarningsMode = companyConfiguration?.earningsConfig?.earningsAccumulationMode;
  const initialPointsIfVisited = companyConfiguration?.rewardsConfig?.pointsIfVisited ?? 0;
  const initialPointsIfNotVisited = companyConfiguration?.rewardsConfig?.pointsIfNotVisited ?? 0;

  const {
    register,
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<GeneralConfigurationFormFields>({
    mode: 'onBlur',
    resolver: yupResolver(generalConfigurationFormSchema),
  });

  useEffect(() => {
    if (company) {
      reset({
        name: company.name ?? '',
        displayName: company.displayName ?? '',
        companyNumber: company.companyNumber ?? '',
        addressLine1: company.address?.addressLine1 ?? '',
        addressLine2: company.address?.addressLine2 ?? '',
        city: company.address?.city ?? '',
        countryCode: company.address?.countryCode ?? company.countryCode ?? '',
        postcode: company.address?.postCode ?? '',
        invoiceRecipientName: company.invoiceRecipientName ?? '',
        invoiceEmail: company.invoiceEmail ?? '',
        vatNumber: company.vatNumber ?? '',
      });
    }
  }, [company, reset]);

  const watchCountryCode = watch('countryCode');

  useEffect(() => {
    setShowAnnualGrossSalary(initialShowAnnualGrossSalary);
  }, [initialShowAnnualGrossSalary]);

  useEffect(() => {
    setSelectedEarningsMode(initialEarningsMode);
  }, [initialEarningsMode]);

  useEffect(() => {
    setPointsIfVisited(String(initialPointsIfVisited));
    setPointsIfNotVisited(String(initialPointsIfNotVisited));
  }, [initialPointsIfVisited, initialPointsIfNotVisited]);

  const hasToggleChanged = showAnnualGrossSalary !== initialShowAnnualGrossSalary;
  const hasEarningsChanged = !!selectedEarningsMode && selectedEarningsMode !== initialEarningsMode;
  const parsedPointsIfVisited = Number(pointsIfVisited);
  const parsedPointsIfNotVisited = Number(pointsIfNotVisited);
  const hasRewardsChanged =
    parsedPointsIfVisited !== initialPointsIfVisited ||
    parsedPointsIfNotVisited !== initialPointsIfNotVisited;
  const isRewardsValid =
    pointsIfVisited.trim() !== '' &&
    Number.isInteger(parsedPointsIfVisited) &&
    parsedPointsIfVisited >= 0 &&
    pointsIfNotVisited.trim() !== '' &&
    Number.isInteger(parsedPointsIfNotVisited) &&
    parsedPointsIfNotVisited >= 0;
  const hasConfigChanged = hasToggleChanged || hasEarningsChanged || hasRewardsChanged;
  const hasErrors = Object.keys(errors).length > 0;
  const isSaveDisabled =
    (!isDirty && !hasConfigChanged) || hasErrors || !isRewardsValid || isSaving;

  const handleSave = async (data: GeneralConfigurationFormFields) => {
    if (!company) return;

    setIsSaving(true);
    try {
      if (isDirty) {
        await updateCompanyAsync({
          id: company.id,
          name: data.name,
          displayName: data.displayName || undefined,
          countryCode: data.countryCode as CountryCode,
          companyNumber: data.companyNumber,
          address: {
            addressLine1: data.addressLine1,
            addressLine2: data.addressLine2,
            city: data.city,
            postCode: data.postcode,
            countryCode: data.countryCode as CountryCode,
          },
          vatNumber: data.vatNumber || undefined,
          invoiceEmail: data.invoiceEmail || undefined,
          invoiceRecipientName: data.invoiceRecipientName || undefined,
        });
      }

      if (hasConfigChanged) {
        await updateCompanyConfigurationAsync({
          id: company.id,
          ...(hasToggleChanged
            ? {
                generalCompanyConfiguration: {
                  showEmployeeAnnualGrossSalary: showAnnualGrossSalary,
                },
              }
            : {}),
          ...(hasEarningsChanged && selectedEarningsMode
            ? { earningsConfig: { earningsAccumulationMode: selectedEarningsMode } }
            : {}),
          ...(hasRewardsChanged
            ? {
                rewardsConfig: {
                  pointsIfVisited: parsedPointsIfVisited,
                  pointsIfNotVisited: parsedPointsIfNotVisited,
                },
              }
            : {}),
        });
      }

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Configuration saved',
        description: 'The general configuration was successfully saved.',
      });

      queryClient.invalidateQueries({ queryKey: ['fetchCompany', company.id] });
      queryClient.invalidateQueries({ queryKey: ['fetchCompanyConfiguration', company.id] });
      reset(data);
    } catch (error) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'It was not possible to save the general configuration.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingCompany || isLoadingConfiguration) {
    return (
      <Center width="full" py="40px">
        <Spinner color="brand.purple" size="xl" />
      </Center>
    );
  }

  const selectedEarningsOption = EARNINGS_ACCUMULATION_MODE_OPTIONS.find(
    (option) => option.value === (selectedEarningsMode ?? initialEarningsMode)
  );

  return (
    <form onSubmit={handleSubmit(handleSave)} noValidate style={{ width: '100%' }}>
      <Flex flexDir="column" gap="20px" alignItems="stretch" w="full">
        <ProfileSection title="Company details" isEditMode maxW="100%">
          <HStack spacing="20px" alignItems="flex-start">
            <FormControl isInvalid={!!errors?.name?.message} isRequired>
              <FormLabel>Company name</FormLabel>
              <Input
                type="text"
                placeholder="Enter company name"
                data-testid="company-name-input"
                {...register('name')}
              />
              <FormErrorMessage>{errors?.name?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors?.displayName?.message}>
              <FormLabel>Company display name</FormLabel>
              <Input
                type="text"
                placeholder="Enter company display name"
                data-testid="company-display-name-input"
                {...register('displayName')}
              />
              <FormErrorMessage>{errors?.displayName?.message}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isInvalid={!!errors?.companyNumber?.message} isRequired>
            <FormLabel>Company number</FormLabel>
            <Input
              type="text"
              placeholder="Enter company number"
              data-testid="company-number-input"
              {...register('companyNumber')}
            />
            <FormErrorMessage>{errors?.companyNumber?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.addressLine1?.message} isRequired>
            <FormLabel>Address line 1</FormLabel>
            <Input
              type="text"
              placeholder="Enter address"
              data-testid="address-line-1-input"
              {...register('addressLine1')}
            />
            <FormErrorMessage>{errors?.addressLine1?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.addressLine2?.message}>
            <FormLabel>Address line 2</FormLabel>
            <Input
              type="text"
              placeholder="Enter address"
              data-testid="address-line-2-input"
              {...register('addressLine2')}
            />
          </FormControl>

          <HStack spacing="20px" alignItems="flex-start">
            <FormControl isInvalid={!!errors?.city?.message} isRequired>
              <FormLabel>Town/City</FormLabel>
              <Input
                type="text"
                placeholder="Enter town/city"
                data-testid="city-input"
                {...register('city')}
              />
              <FormErrorMessage>{errors?.city?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors?.countryCode?.message} isRequired>
              <FormLabel>Country</FormLabel>
              <Controller
                control={control}
                name="countryCode"
                render={({ field: { onChange, onBlur } }) => (
                  <DropdownInput
                    key={watchCountryCode}
                    placeholder="Select a country"
                    options={COUNTRY_OPTIONS}
                    defaultValue={COUNTRY_OPTIONS.find(
                      (option) => option.value === watchCountryCode
                    )}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
              <FormErrorMessage>{errors?.countryCode?.message}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isInvalid={!!errors?.postcode?.message} isRequired>
            <FormLabel>Postcode</FormLabel>
            <Input
              type="text"
              placeholder="Enter postcode"
              data-testid="postcode-input"
              {...register('postcode')}
            />
            <FormErrorMessage>{errors?.postcode?.message}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel>FX fee group</FormLabel>
            <Input
              type="text"
              isReadOnly
              value={CompanyFeeGroupLabels[company?.feeGroup || 'DEFAULT_FEE']}
              data-testid="fee-group-input"
            />
            <FormHelperText>FX fee group update functionality is not implemented.</FormHelperText>
          </FormControl>
        </ProfileSection>

        <ProfileSection title="Billing details" isEditMode maxW="100%">
          <FormControl isInvalid={!!errors?.invoiceRecipientName?.message}>
            <FormLabel>Invoice recipient name</FormLabel>
            <Input
              type="text"
              placeholder="Enter invoice recipient name"
              data-testid="invoice-recipient-name-input"
              {...register('invoiceRecipientName')}
            />
            <FormErrorMessage>{errors?.invoiceRecipientName?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.invoiceEmail?.message}>
            <FormLabel>Invoice email</FormLabel>
            <Input
              type="email"
              placeholder="Enter invoice email"
              data-testid="invoice-email-input"
              {...register('invoiceEmail')}
            />
            <FormErrorMessage>{errors?.invoiceEmail?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.vatNumber?.message}>
            <FormLabel>VAT number</FormLabel>
            <Input
              type="text"
              placeholder="Enter VAT number"
              data-testid="vat-number-input"
              {...register('vatNumber')}
            />
            <FormErrorMessage>{errors?.vatNumber?.message}</FormErrorMessage>
          </FormControl>
        </ProfileSection>

        <ProfileSection title="Earnings" isEditMode maxW="100%">
          <FormControl>
            <FormLabel>Earnings accumulation mode</FormLabel>
            <DropdownInput
              key={initialEarningsMode ?? 'none'}
              placeholder="Select accumulation mode"
              options={EARNINGS_ACCUMULATION_MODE_OPTIONS}
              defaultValue={selectedEarningsOption}
              onChange={(value) => setSelectedEarningsMode(value as EarningsAccumulationMode)}
            />
            <VStack align="flex-start" spacing="4px" mt="10px">
              <Text textStyle="extraSmallRegular" color="brand.darkGray">
                {EARNINGS_ACCUMULATION_MODE_DESCRIPTIONS.CUMULATIVE}
              </Text>
              <Text textStyle="extraSmallRegular" color="brand.darkGray">
                {EARNINGS_ACCUMULATION_MODE_DESCRIPTIONS.OVERRIDE}
              </Text>
            </VStack>
          </FormControl>
        </ProfileSection>

        <ProfileSection title="Rewards" isEditMode maxW="100%">
          <Text textStyle="extraSmallRegular" color="brand.darkGray">
            Reward points employees earn on their card spend, set as points awarded per £1 spent.
            The rate used depends on whether the purchase came from a merchant the employee reached
            through the Aslan app.
          </Text>
          <HStack spacing="20px" alignItems="flex-start">
            <FormControl>
              <FormLabel>Points if visited</FormLabel>
              <Input
                type="number"
                min={0}
                value={pointsIfVisited}
                onChange={(event) => setPointsIfVisited(event.target.value)}
                data-testid="points-if-visited-input"
              />
              <FormHelperText>
                Points per £1 when the purchase is from a merchant the employee reached via the
                Aslan app (a tracked click). Usually the higher rate, to reward shopping through the
                app.
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Points if not visited</FormLabel>
              <Input
                type="number"
                min={0}
                value={pointsIfNotVisited}
                onChange={(event) => setPointsIfNotVisited(event.target.value)}
                data-testid="points-if-not-visited-input"
              />
              <FormHelperText>
                Points per £1 when there is no tracked merchant click — the employee spent at the
                merchant without going through the Aslan app.
              </FormHelperText>
            </FormControl>
          </HStack>
        </ProfileSection>

        <ProfileSection title="Salary visibility" isEditMode maxW="100%">
          <Flex w="full" align="center" justify="space-between" gap="15px">
            <Flex flexDir="column">
              <Text textStyle="smallMedium" color="brand.black">
                Show employee annual gross salary
              </Text>
              <Text textStyle="extraSmallRegular" color="brand.darkGray">
                Allow company admins to view employees&apos; annual gross salary in the client
                portal.
              </Text>
            </Flex>
            <Switch
              colorScheme="badge.invited"
              size="lg"
              isChecked={showAnnualGrossSalary}
              onChange={(event) => setShowAnnualGrossSalary(event.target.checked)}
              data-testid="show-annual-gross-salary-switch"
            />
          </Flex>
        </ProfileSection>
      </Flex>

      <Flex mt="30px">
        <Button
          leftIcon={<CheckOutline boxSize="20px" />}
          size="sm"
          type="submit"
          isLoading={isSaving}
          isDisabled={isSaveDisabled}
          data-testid="save-general-config-button"
        >
          Save changes
        </Button>
      </Flex>
    </form>
  );
}

export { GeneralTab };
